import styled, { css, keyframes, type DefaultTheme } from 'styled-components';
import Modal, { BaseModalBackground } from 'styled-react-modal';
import { breakpointUp } from 'styles/breakpoints';
import P from 'components/typography/text/P';
import H1 from 'components/typography/headings/H1';
import H2 from 'components/typography/headings/H2';
import H3 from 'components/typography/headings/H3';
import H4 from 'components/typography/headings/H4';
import H5 from 'components/typography/headings/H5';

type StyledModalProps = {
    $large?: boolean;
    $closing?: boolean;
};

const showModal = keyframes`
  0% { transform: translate(0, -50px); opacity: 0; animation-timing-function: ease-out; }
  50% { transform: translate(0, -50px); opacity: 0; animation-timing-function: ease-out; }
  100% { transform: translate(0, 0); opacity: 1; }
`;

const hideModal = keyframes`
  0% { transform: translate(0, 0); opacity: 1; animation-timing-function: ease-in; }
  33% { transform: translate(0, -50px); opacity: 0; }
  100% { transform: translate(0, -50px); opacity: 0; }
`;

export const StyledModal = (Modal as any).styled<StyledModalProps>`
  position: relative;
  width: 80vw;
  max-width: 80%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  background-color: ${({ theme }: { theme: DefaultTheme }) => theme.colors.modals.background};
  border-radius: ${({ theme }: { theme: DefaultTheme }) => theme.borderRadius.medium};
  color: ${({ theme }: { theme: DefaultTheme }) => theme.colors.modals.text};
  pointer-events: auto;
  animation: ${showModal} 600ms;

  ${breakpointUp.md`
    max-width: 500px;
  `}

  ${({ $large }: StyledModalProps) =>
      $large &&
      breakpointUp.md`
      max-width: 80%;
    `}

  ${({ $closing }: StyledModalProps) =>
      $closing &&
      css`
          animation: ${hideModal} 750ms;
      `}

  ${P}, ${H1}, ${H2}, ${H3}, ${H4}, ${H5} {
    color: ${({ theme }: { theme: DefaultTheme }) => theme.colors.modals.text};
  }
`;

export const ModalBackground = styled(BaseModalBackground)<{ backgroundVisible?: boolean }>`
    background-color: ${({ theme, backgroundVisible }: { theme: DefaultTheme; backgroundVisible?: boolean }) =>
        backgroundVisible ? theme.colors.modals.overlay : 'rgba(0,0,0,0)'};
    transition: background-color linear 250ms;
`;

export const StyledModalHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    width: calc(100% - (${({ theme }: { theme: DefaultTheme }) => theme.space[5]} * 2));
    margin: 0 ${({ theme }: { theme: DefaultTheme }) => theme.space[5]};
    padding: ${({ theme }: { theme: DefaultTheme }) => theme.space[5]} 0;
    border-top-left-radius: ${({ theme }: { theme: DefaultTheme }) => theme.borderRadius.medium};
    border-top-right-radius: ${({ theme }: { theme: DefaultTheme }) => theme.borderRadius.medium};
    border-bottom: 1px solid ${({ theme }: { theme: DefaultTheme }) => theme.colors.modals.separators};
`;

export const StyledModalBody = styled.div`
    position: relative;
    flex: 1 1 auto;
    width: calc(100% - (${({ theme }: { theme: DefaultTheme }) => theme.space[5]} * 2));
    max-height: calc(100vh - 250px);
    overflow-y: auto;
    margin: ${({ theme }: { theme: DefaultTheme }) => theme.space[5]} 0;

    a {
        color: ${({ theme }: { theme: DefaultTheme }) => theme.colors.modals.link.text};
        &:active {
            color: ${({ theme }: { theme: DefaultTheme }) => theme.colors.modals.link.text};
        }
        &:hover,
        &:focus {
            color: ${({ theme }: { theme: DefaultTheme }) => theme.colors.modals.link.hover.text};
        }
    }
`;

export const StyledModalFooter = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: calc(100% - (${({ theme }: { theme: DefaultTheme }) => theme.space[5]} * 2));
    margin: 0 ${({ theme }: { theme: DefaultTheme }) => theme.space[5]};
    padding: ${({ theme }: { theme: DefaultTheme }) => theme.space[5]} 0;
    border-bottom-left-radius: ${({ theme }: { theme: DefaultTheme }) => theme.borderRadius.medium};
    border-bottom-right-radius: ${({ theme }: { theme: DefaultTheme }) => theme.borderRadius.medium};
    border-top: 1px solid ${({ theme }: { theme: DefaultTheme }) => theme.colors.modals.separators};
`;

export const StyledModalCloseButton = styled.button`
    height: 18px;
    width: 18px;
    position: absolute;
    top: ${({ theme }: { theme: DefaultTheme }) => theme.space[5]};
    right: ${({ theme }: { theme: DefaultTheme }) => theme.space[5]};
    padding: 0;
    background-color: transparent;
    border: none;
    outline: none;

    @media (hover: none) and (pointer: coarse) {
        height: 50px;
        width: 50px;
        top: 0;
        right: 4px;
        padding: ${({ theme }: { theme: DefaultTheme }) => theme.space[5]};
    }

    svg {
        cursor: pointer;
    }
    &:hover svg {
        opacity: 0.8;
    }
    span {
        display: none;
    }
`;
